/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Slim, type-safe wrapper around Angular's HttpClient.
 *
 * Replaces the legacy RequestService. Returns Observable<T> directly —
 * no Promise wrapper, no boolean flag. Error normalisation is handled
 * centrally by the errorNormalizationInterceptor.
 *
 * RETRY STRATEGY:
 * Idempotent read operations (GET, HEAD) are automatically retried up to
 * MAX_RETRIES times using exponential backoff (1 s, 2 s). Retries are skipped
 * for client errors (4xx) because repeating the same request would always yield
 * the same failure. Mutation methods (POST, PUT, PATCH, DELETE) are never
 * retried automatically to prevent accidental duplicate writes.
 */

import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {Observable, map, retry, timer} from 'rxjs';

import {FileResponse} from '../models/file-response';
import {extractFilename} from '../utils/extract-filename';
import {CacheGetOptions, RequestCacheService} from './request-cache.service';

/** Maximum number of automatic retry attempts for idempotent requests. */
const MAX_RETRIES = 2;

/** Optional per-request HTTP options accepted by ApiClient methods. */
export interface HttpRequestOptions {
  headers?: HttpHeaders;
}

/**
 * RxJS `retry` configuration for idempotent GET requests.
 *
 * - Retries at most MAX_RETRIES times.
 * - Uses exponential backoff: attempt 1 → 1 s delay, attempt 2 → 2 s delay.
 * - Skips retry for 4xx client errors — repeating them is pointless.
 */
const getRetryConfig = {
  count: MAX_RETRIES,
  delay: (error: HttpErrorResponse, attempt: number) => {
    if (error.status >= 400 && error.status < 500) {
      // Client errors are deterministic — no point retrying.
      throw error;
    }
    return timer(attempt * 1_000);
  },
};

@Injectable({providedIn: 'root'})
export class ApiClient {
  private readonly http = inject(HttpClient);
  private readonly cache = inject(RequestCacheService);

  /**
   * Performs a GET request expecting a JSON response body.
   * Automatically retried up to MAX_RETRIES times on network or server errors.
   */
  get<T>(path: string, options?: HttpRequestOptions): Observable<T> {
    return this.http.get<T>(path, options).pipe(retry(getRetryConfig));
  }

  /**
   * Deduplicating, TTL-cached GET via the central RequestCacheService.
   *
   * - Concurrent calls with the same `path` share one in-flight HTTP request.
   * - The response is replayed to late subscribers via `shareReplay(1)`.
   * - The entry expires after `cacheOptions.ttlMs` (default: 5 min). Expired
   *   entries trigger a fresh HTTP call on the next access.
   * - On HTTP error the entry is evicted immediately so the next call retries.
   *
   * Use this instead of `get()` for lookup/reference data that is:
   *   • requested by multiple components simultaneously (schemas, feature flags)
   *   • expensive to fetch but changes rarely
   *
   * To invalidate manually after a mutation:
   *   `this.cache.invalidate(url)` or `this.cache.invalidateByPrefix(prefix)`
   *
   * @param path         - Full request URL, used as the cache key.
   * @param options      - Optional HTTP headers.
   * @param cacheOptions - Optional TTL override (`ttlMs`).
   */
  getShared<T>(
    path: string,
    options?: HttpRequestOptions,
    cacheOptions?: CacheGetOptions,
  ): Observable<T> {
    return this.cache.get<T>(
      path,
      () => this.http.get<T>(path, options).pipe(retry(getRetryConfig)),
      cacheOptions,
    );
  }

  /**
   * Performs a GET request expecting a plain-text response body.
   * Use for CSV or other text-based exports.
   * Automatically retried up to MAX_RETRIES times on network or server errors.
   */
  getText(path: string, options?: HttpRequestOptions): Observable<string> {
    return this.http
      .get(path, {...options, responseType: 'text'})
      .pipe(retry(getRetryConfig));
  }

  /**
   * Performs a GET request expecting a binary (Blob) response.
   * Automatically extracts the filename from the Content-Disposition header.
   * Automatically retried up to MAX_RETRIES times on network or server errors.
   */
  getFile(
    path: string,
    options?: HttpRequestOptions,
  ): Observable<FileResponse> {
    return this.http
      .get(path, {...options, responseType: 'blob', observe: 'response'})
      .pipe(
        retry(getRetryConfig),
        map((res: HttpResponse<Blob>) => ({
          blob: res.body ?? new Blob(),
          filename: extractFilename(res.headers.get('Content-Disposition')),
        })),
      );
  }

  /**
   * Performs a POST request.
   * NOT retried — mutations must not be duplicated automatically.
   */
  post<T>(
    path: string,
    body: unknown = null,
    options?: HttpRequestOptions,
  ): Observable<T> {
    return this.http.post<T>(path, body, options);
  }

  /**
   * Performs a PUT request.
   * NOT retried — mutations must not be duplicated automatically.
   */
  put<T>(
    path: string,
    body: unknown,
    options?: HttpRequestOptions,
  ): Observable<T> {
    return this.http.put<T>(path, body, options);
  }

  /**
   * Performs a PATCH request.
   * NOT retried — mutations must not be duplicated automatically.
   */
  patch<T>(
    path: string,
    body: unknown,
    options?: HttpRequestOptions,
  ): Observable<T> {
    return this.http.patch<T>(path, body, options);
  }

  /**
   * Performs a DELETE request with an optional request body.
   * NOT retried — mutations must not be duplicated automatically.
   */
  delete<T>(
    path: string,
    body?: unknown,
    options?: HttpRequestOptions,
  ): Observable<T> {
    return this.http.request<T>('DELETE', path, {...options, body});
  }
}
